import { randomID } from './S';
import axios from 'axios';  
import { message } from 'antd';
import { globalStore } from '../GlobalStore';
import { Storage } from './Storage';

// 定义泛型 Request 接口
export interface Request<T> {
    requestId: string;
    traceId: string;
    token: string; 
    parameter: T;
}

export interface Response<T>{
    success: boolean;
    result: T;
    costTime: number;
    errorCode: string,
    errorMessages: string
}

const navigateWrapper = {
    nav: null as null | ((path: string) => void)
};
 
// 判断是否为日期字符串
const isDateString = (value: any): boolean => {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value);
};

// 将对象中的所有日期字符串转换为 Date 对象
const convertDates = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map(convertDates);
    } else if (obj && typeof obj === 'object') {
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                if (isDateString(obj[key])) {
                    obj[key] = new Date(obj[key]);
                } else if (typeof obj[key] === 'object') {
                    obj[key] = convertDates(obj[key]);
                }
            }
        }
    }
    return obj;
};

// 将 Date 对象转换为字符串，格式为 "YYYY-MM-DD HH:MM:SS"
const formatDateToString = (date: Date): string => {
    if(!date) return "未知";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// 遍历对象，将所有 Date 类型转换为字符串
const convertDatesToString = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map(convertDatesToString);
    } else if (obj && typeof obj === 'object') {
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                if (obj[key] instanceof Date) {
                    obj[key] = formatDateToString(obj[key]);
                } else if (typeof obj[key] === 'object') {
                    obj[key] = convertDatesToString(obj[key]);
                }
            }
        }
    }
    return obj;
};

export const API_DOMAIN = process.env.REACT_APP_API_DOMAIN;

const joinApiUrl = (domain: string | undefined, path: string): string => {
    if (!domain) {
        return path.startsWith('/') ? path : `/${path}`;
    }
    const normalizedDomain = domain.replace(/\/+$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${normalizedDomain}${normalizedPath}`;
};

console.log('API URL:', API_DOMAIN || '(dev proxy)');

type PostOptions = {
    /** 为 true 时不弹出 0000007 等全局 toast（如 IM 拉取头像） */
    quiet?: boolean;
    /** 便于定位 0000007 等业务错误来源 */
    logTag?: string;
};

// 通用的请求方法
export const post = async <T, R>(
    url: string,
    inputParameter: T,
    options?: PostOptions,
): Promise<R|string> => {
    let status = false;
    let result: R | null = null;
    let error: any = null;
    let errorCode: string = "";

    try {    
        const traceId: string = randomID(); 
        const token: string | null = await Storage.getItem('token'); 
        inputParameter = convertDatesToString(inputParameter);
        const requestData: Request<T> = {
            requestId: traceId,
            traceId: traceId,
            token: token as string, 
            parameter: inputParameter
        }; 

        const response = await axios.post<Response<R>>(joinApiUrl(API_DOMAIN, url), requestData);
        status = response.data.success;

        if (status) {
            // 转换所有日期字段
            result = convertDates(response.data.result);
        } else {
            errorCode = response.data.errorCode;
            if (errorCode === '0000111') {
                // token 过期或未登录，静默清除登录状态，不自动弹登录框
                if (globalStore.isLoggedIn) {
                    globalStore.isLoggedIn = false;
                    globalStore.currentBuyer = null;
                }
            }
            const errorMsgs = response.data.errorMessages;
            if (errorCode === '0000007') {
                console.error('[ApiUtil] 0000007 该用户不存在 toast 关联请求', {
                    logTag: options?.logTag,
                    quiet: options?.quiet,
                    url,
                    parameter: inputParameter,
                    traceId,
                    errorMessages: errorMsgs,
                });
                if (!options?.quiet) {
                    message.warning("该用户不存在！请先注册！");
                }
            }
            // 处理错误信息，可能是字符串或数组
            if (Array.isArray(errorMsgs) && errorMsgs.length > 0) {
                error = errorMsgs[0];
            } else if (typeof errorMsgs === 'string') {
                error = errorMsgs;
            } else {
                error = `错误码：${errorCode}`;
            }
        }
    } catch (e) {
        error = e;
        throw new Error(errorCode || String(e));
    }

    if (status) {
        return result as R;
    } else {
        // 返回错误码和错误信息的组合，格式：errorCode|errorMessage
        const errorInfo = error ? `${errorCode}|${error}` : errorCode;
        return errorInfo as string;
    }
};